import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
} from "@components/ui/table";
import { Trans, useLingui } from "@lingui/react/macro";
import ReportRow from "./ReportRow";
import { Report } from "@/types/Report";
import { Organization } from "@/types/Organization";
import { User } from "@/types/User";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  addReportSubscription,
  deleteReportSubscription,
  getOrganizationReports,
  getOrganizationUserReportSubscriptions,
} from "@/api/endpoints/organization";
import { ReportSubscription } from "@/types/ReportSubscription";
import { Skeleton } from "@components/ui/skeleton";
import ErrorComponent from "@components/ui/error";

type ReportSubscriptionsTableProps = {
  isShowingSubscribers?: boolean;
  orgUuid: Organization["uuid"];
  userId: User["id"];
};

export default function ReportSubscriptionsTable({
  isShowingSubscribers = true,
  orgUuid,
  userId,
  ...rest
}: ReportSubscriptionsTableProps & React.ComponentProps<"table">) {
  const { t } = useLingui();

  // Server state
  const queryClient = useQueryClient();

  const {
    data: reportsData,
    error: reportsError,
    isLoading: isReportsLoading,
  } = useQuery({
    queryKey: [orgUuid, "reports"],
    queryFn: () => getOrganizationReports(),
  });

  const {
    data: reportSubscriptionsData,
    error: reportSubscriptionsError,
    isLoading: isReportSubscriptionsLoading,
  } = useQuery({
    queryKey: [orgUuid, userId, "report-subscriptions"],
    queryFn: () => {
      return getOrganizationUserReportSubscriptions({
        organization_uuid: orgUuid,
        user_id: userId,
      });
    },
  });

  function refetchData() {
    queryClient.invalidateQueries({
      queryKey: [orgUuid, userId, "report-subscriptions"],
    });
    queryClient.invalidateQueries({
      queryKey: [orgUuid, "reports"],
    });
  }

  const {
    mutate: mutateCreateReportSubscription,
    isPending: isCreatingReportSubscription,
  } = useMutation({
    mutationFn: (reportUuid: Report["uuid"]) => {
      return addReportSubscription({
        report_uuid: reportUuid,
        organization_uuid: orgUuid,
        user_id: userId,
      });
    },
    onSuccess: (response) => {
      if (!response.ok) {
        throw new Error();
      }

      refetchData();
    },
    onError: () => {
      toast.error(t`Kunde inte lägga till prenumerationen`);
    },
  });

  const {
    mutate: mutateDeleteReportSubscription,
    isPending: isDeletingReportSubscription,
  } = useMutation({
    mutationFn: ({
      subId,
      reportUuid,
    }: {
      subId: ReportSubscription["id"];
      reportUuid: Report["uuid"];
    }) => {
      return deleteReportSubscription({
        organization_uuid: orgUuid,
        report_uuid: reportUuid,
        user_id: userId,
        sub_id: subId,
      });
    },
    onSuccess: (response) => {
      if (!response.ok) {
        throw new Error();
      }

      refetchData();
    },
    onError: () => {
      toast.error(t`Kunde inte ta bort prenumerationen`);
    },
  });

  // Handlers
  const handleCreateReportSubscription = (reportUuid: Report["uuid"]) => {
    mutateCreateReportSubscription(reportUuid);
  };

  const handleDeleteReportSubscription = ({
    subId,
    reportUuid,
  }: {
    subId: ReportSubscription["id"];
    reportUuid: Report["uuid"];
  }) => {
    mutateDeleteReportSubscription({
      reportUuid,
      subId,
    });
  };

  if (reportsData?.ok && reportSubscriptionsData?.ok) {
    return (
      <Table {...rest}>
        <TableHeader>
          <TableRow>
            <TableHead>
              <Trans>Rapportens titel</Trans>
            </TableHead>

            <TableHead>
              <Trans>Prenumererar</Trans>
            </TableHead>

            {isShowingSubscribers && (
              <TableHead>
                <Trans>Prenumeranter</Trans>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {reportsData.data.map((report) => (
            <ReportRow
              userId={userId}
              orgUuid={orgUuid}
              report={report}
              key={report.uuid}
              isShowingSubscribers={isShowingSubscribers}
              isDeletingReportSubscription={isDeletingReportSubscription}
              isCreatingReportSubscription={isCreatingReportSubscription}
              reportSubscriptions={reportSubscriptionsData.data.reports.filter(
                (rs) => rs.uuid === report.uuid
              )}
              onCreateAlertSubscription={() => {
                handleCreateReportSubscription(report.uuid);
              }}
              onDeleteAlertSubscription={(subId) => {
                handleDeleteReportSubscription({
                  reportUuid: report.uuid,
                  subId,
                });
              }}
            />
          ))}
        </TableBody>
      </Table>
    );
  }

  if (isReportsLoading || isReportSubscriptionsLoading) {
    return <Skeleton className="h-56 w-full" />;
  }

  if (!reportsData?.ok || !reportSubscriptionsData?.ok) {
    return (
      <ErrorComponent
        heading={t`Åh nej!`}
        description={t`Något gick snett med att ladda rapporterna. Vänligen försök igen senare.`}
        error={reportsError || reportSubscriptionsError}
      />
    );
  }

  return null;
}
